/* eslint eslint-comments/no-use: off */

import {isFeatureEnabled} from '@github-ui/feature-flags'
import {LABELS} from '@github-ui/item-picker-base/constants/labels'
import {type ExtendedItemProps, ItemPicker} from '@github-ui/item-picker-base/ItemPicker'
import {LazyItemPicker} from '@github-ui/item-picker-base/LazyItemPicker'
import {useItemPickerErrorFallback} from '@github-ui/item-picker-base/useItemPickerErrorFallback'
import {DEFAULT_RELAY_CACHE_TTL} from '@github-ui/relay-query-cache/helpers'
import {clientSideRelayFetchQueryRetainedWithCache} from '@github-ui/relay-query-cache/query-helpers'
import {type RefObject, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {graphql, readInlineData, useRelayEnvironment} from 'react-relay'

import type {IssueMultiSelectFieldPickerFieldQuery} from './__generated__/IssueMultiSelectFieldPickerFieldQuery.graphql'
import type {
  IssueMultiSelectFieldPickerOption$data,
  IssueMultiSelectFieldPickerOption$key,
} from './__generated__/IssueMultiSelectFieldPickerOption.graphql'
import {createIssueSingleSelectFieldPickerItemLeadingVisual} from './IssueFieldLeadingVisual'

export type IssueFieldMultiSelectOption = Omit<IssueMultiSelectFieldPickerOption$data, ' $fragmentType' | 'color'> & {
  color: 'BLUE' | 'GRAY' | 'GREEN' | 'ORANGE' | 'PINK' | 'PURPLE' | 'RED' | 'YELLOW'
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const IssueMultiSelectFieldPickerOptionFragment = graphql`
  fragment IssueMultiSelectFieldPickerOption on IssueFieldSingleSelectOption @inline {
    id
    name
    description
    color
  }
`

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const IssueMultiSelectFieldPickerFieldGraphqlQuery = graphql`
  query IssueMultiSelectFieldPickerFieldQuery($id: ID!) {
    node(id: $id) {
      ... on IssueFieldMultiSelect {
        options {
          ...IssueMultiSelectFieldPickerOption
        }
      }
    }
  }
`

export type IssueMultiSelectFieldPickerProps = {
  title?: string
  width?: 'small' | 'medium' | 'large'
  fieldId: string
  fieldName?: string
  selectedOptions: IssueFieldMultiSelectOption[] | null
  readonly: boolean
  insidePortal?: boolean
  anchorElement: (
    props: React.HTMLAttributes<HTMLElement>,
    ref?: RefObject<HTMLButtonElement | null>,
  ) => React.ReactElement
  onSelectionChange: (selectedOption: IssueFieldMultiSelectOption[]) => void
  onOpen?: () => void
  onClose?: () => void
  onIssueUpdate?: () => void
  /**
   * Whether to render the multi-select field picker as a nested select panel (true) versus a standalone select
   * panel (false; default).
   */
  nested?: boolean
  ariaLabelledBy?: string
  /**
   * Whether this panel is lazily loaded, when the anchor element is clicked. When set to false, the
   * panel will be loaded immediately. This is used when we want to immediately select an option
   * after picking a multi select field.
   */
  isLazy: boolean
  /**
   * Whether this panel should be open when initially rendered
   */
  shouldOpen?: boolean
}

type ItemPickerWrapperProps = Omit<IssueMultiSelectFieldPickerProps, 'isLazy'> & {
  values: IssueMultiSelectFieldPickerOption$key[]
  isLoading: boolean
}

export function IssueMultiSelectFieldPicker({
  anchorElement,
  isLazy,
  shouldOpen = false,
  ...props
}: IssueMultiSelectFieldPickerProps) {
  if (isLazy && !shouldOpen) {
    return (
      <LazyItemPicker
        anchorElement={(anchorProps, ref) => anchorElement(anchorProps, ref)}
        createChild={() => (
          <IssueMultiSelectFieldPickerFetcher
            anchorElement={(anchorProps, ref) => anchorElement(anchorProps, ref)}
            {...props}
          />
        )}
      />
    )
  } else {
    return (
      <IssueMultiSelectFieldPickerFetcher
        anchorElement={(anchorProps, ref) => anchorElement(anchorProps, ref)}
        {...props}
      />
    )
  }
}

function IssueMultiSelectFieldPickerFetcher({
  fieldId,
  anchorElement,
  ...props
}: Omit<IssueMultiSelectFieldPickerProps, 'isLazy'>) {
  const environment = useRelayEnvironment()
  const [isLoading, setIsLoading] = useState(true)
  const [fetchKey, setFetchKey] = useState(0)
  const [isError, setIsError] = useState(false)
  const [data, setData] = useState<IssueMultiSelectFieldPickerOption$key[]>([])
  const [requestKey, setRequestKey] = useState({fieldId, fetchKey})

  if (requestKey.fieldId !== fieldId || requestKey.fetchKey !== fetchKey) {
    setRequestKey({fieldId, fetchKey})
    setIsLoading(true)
    setIsError(false)
  }

  useEffect(() => {
    const subscription = clientSideRelayFetchQueryRetainedWithCache<IssueMultiSelectFieldPickerFieldQuery>(
      {
        environment,
        query: IssueMultiSelectFieldPickerFieldGraphqlQuery,
        variables: {id: fieldId},
      },
      DEFAULT_RELAY_CACHE_TTL,
    ).subscribe({
      next: internalData => {
        const keys = internalData.node?.options as IssueMultiSelectFieldPickerOption$key[]
        setData(keys ?? [])
        setIsLoading(false)
        setIsError(false)
      },
      error: () => {
        setIsError(true)
        setIsLoading(false)
      },
    })
    return () => subscription.unsubscribe()
  }, [environment, fetchKey, fieldId])

  const {createFallbackComponent} = useItemPickerErrorFallback({
    errorMessage: LABELS.cantEditItems('fields'),
    anchorElement,
    open: true,
  })

  if (isError) {
    return createFallbackComponent(() => setFetchKey(current => current + 1))
  }

  return (
    <ItemPickerWrapper fieldId={fieldId} values={data} isLoading={isLoading} anchorElement={anchorElement} {...props} />
  )
}

function ItemPickerWrapper({
  title = LABELS.fieldMultiSelectHeader,
  width = 'medium',
  values,
  fieldName,
  selectedOptions,
  onSelectionChange,
  onOpen,
  onClose,
  insidePortal,
  anchorElement,
  isLoading,
}: ItemPickerWrapperProps) {
  const [filter, setFilter] = useState('')

  // Use prop fieldName or fallback to default title
  const dynamicTitle = fieldName ? LABELS.fieldMultiSelectHeaderWithName(fieldName) : title

  const fetchedIssueFieldOptions = useMemo(
    () =>
      values.flatMap(a =>
        // eslint-disable-next-line no-restricted-syntax
        a ? [readInlineData<IssueMultiSelectFieldPickerOption$key>(IssueMultiSelectFieldPickerOptionFragment, a)] : [],
      ) as IssueFieldMultiSelectOption[],
    [values],
  )

  const items = useMemo(() => {
    if (!filter) return fetchedIssueFieldOptions
    return fetchedIssueFieldOptions.filter(l => l.name && l.name.toLowerCase().indexOf(filter.toLowerCase()) >= 0)
  }, [fetchedIssueFieldOptions, filter])

  const filterItems = useCallback((value: string) => {
    setFilter(value)
  }, [])

  const getItemKey = useCallback((option: IssueFieldMultiSelectOption) => option.id || '', [])

  const convertToItemProps = useCallback(
    (option: IssueFieldMultiSelectOption): ExtendedItemProps<IssueFieldMultiSelectOption> => {
      return {
        id: option.id,
        text: option.name,
        description: option.description || '',
        descriptionVariant: 'block',
        leadingVisual: createIssueSingleSelectFieldPickerItemLeadingVisual(option.color),
        source: option,
      }
    },
    [],
  )

  const initialSelectedItems = useMemo(() => {
    if (!selectedOptions || selectedOptions.length === 0) return []
    const selectedIds = new Set(selectedOptions.map(o => o.id))
    return fetchedIssueFieldOptions.filter(item => selectedIds.has(item.id))
  }, [fetchedIssueFieldOptions, selectedOptions])

  const anchorRef = useRef<HTMLButtonElement>(null)

  const showNoMatchMessage = useMemo(
    () => !isLoading && items.length === 0 && filter.length > 0,
    [isLoading, items, filter],
  )

  const responsiveOnNarrowEnabled = isFeatureEnabled('issues_react_select_panel_fullscreen_on_narrow')
  return (
    <ItemPicker
      loading={isLoading}
      items={items}
      initialSelectedItems={initialSelectedItems}
      filterItems={filterItems}
      title={dynamicTitle}
      getItemKey={getItemKey}
      convertToItemProps={convertToItemProps}
      placeholderText={`Filter options`}
      selectionVariant="multiple"
      onSelectionChange={selectedItems =>
        onSelectionChange(selectedItems && selectedItems.length > 0 ? selectedItems : [])
      }
      onOpen={onOpen}
      onClose={onClose}
      renderAnchor={anchorProps => anchorElement(anchorProps, anchorRef)}
      insidePortal={insidePortal}
      height={'large'}
      width={width}
      resultListAriaLabel={'Issue field option results'}
      triggerOpen
      selectPanelRef={anchorRef}
      improvedNoMatchAccessibility={showNoMatchMessage}
      noMatchMessage={
        showNoMatchMessage
          ? {
              title: 'No option found',
              variant: 'empty',
              body: 'Try searching with a different query for results.',
            }
          : undefined
      }
      responsiveOnNarrow={responsiveOnNarrowEnabled}
    />
  )
}
